
// import Reverso from './reverso-api-local';
import { Reverso } from './reverso-api-local'
import compatibility from './reverso-api-local/src/utils/languages/compatibility.js'

// App language code -> Reverso's. Reverso only covers these; anything else
// (e.g. the native-only fa, vi) goes straight to Google — see translate().
const languageMap = {
    'en': 'english' as const,
    'de': 'german' as const,
    'pl': 'polish' as const,
    'ru': 'russian' as const,
    'es': 'spanish' as const,
    'fr': 'french' as const,
    'uk': 'ukrainian' as const,
    'ar': 'arabic' as const,
    'ja': 'japanese' as const,
    'pt': 'portuguese' as const,
    'nl': 'dutch' as const,
    'it': 'italian' as const,
    'tr': 'turkish' as const,
    'ro': 'romanian' as const,
};
type ReversoLanguage = keyof typeof languageMap;

/**
 * Whether Reverso can translate between these two app language codes. Both being in
 * languageMap isn't enough — Reverso doesn't cover every pair (e.g. ro↔uk; Ukrainian is
 * only a target from English), and getTranslation rejects those, so check its own table.
 */
export function reversoSupports(from: string, to: string): boolean {
    if (!(from in languageMap) || !(to in languageMap)) return false;
    const source = languageMap[from as ReversoLanguage];
    const target = languageMap[to as ReversoLanguage];
    return compatibility.translation
        .find((e: { name: string }) => e.name === source)
        ?.compatible_with.includes(target) ?? false;
}
const reverso = new Reverso();

type TranslateResult = { translations: string[]; [key: string]: any };

function getReversoTranslation(text: string, from: ReversoLanguage, to: ReversoLanguage): Promise<TranslateResult> {
    return new Promise((resolve, reject) => {
        // getTranslation is itself `async`, so a bug inside it (see reverso.js) rejects the
        // Promise it returns rather than throwing synchronously here — a surrounding try/catch
        // around this whole call could never actually catch that. Without a .catch on the call
        // itself, that rejection went nowhere: the cb callback below never fired, and this
        // wrapper Promise sat unresolved forever (the "stuck on Translating…" bug).
        reverso.getTranslation(
            text,
            languageMap[from],
            languageMap[to],
            (err: Error | null, response: TranslateResult) => {
                if (err) return reject(new Error(err.message));
                resolve(response);
            }
        ).catch((error: Error) => reject(new Error(error.message || 'Translation failed')));
    });
}

// Google's translate_a/single endpoint — the same unauthenticated one browser extensions and
// the Google Translate website itself use client-side; no API key, so no backend proxy needed
// (matches this file's existing pattern of calling Reverso's own unofficial API directly from
// the browser). Google's ISO codes ('en', 'de', ...) already match this app's own language
// codes, unlike Reverso's full-name mapping above.
async function getGoogleTranslation(text: string, from: string, to: string): Promise<TranslateResult> {
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${from}&tl=${to}&dt=t&q=${encodeURIComponent(text)}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Google Translate ${res.status}`);
    const data = await res.json();
    // Response shape: [[[translatedChunk, sourceChunk, ...], ...], ...] — long input gets
    // split across multiple chunks that need joining back into one string.
    const translated = (Array.isArray(data?.[0]) ? data[0] : [])
        .map((segment: any[]) => segment?.[0] ?? '')
        .join('');
    if (!translated) throw new Error('Empty translation received');
    return { translations: [translated] };
}

/**
 * Client-side, on-demand sentence/text translation via Google's public translate_a/single
 * endpoint — used by lesson sections' TranslateToggle as the fallback when a lesson has no
 * LLM-baked "_native" field for a given piece of text (either the admin's "include translation"
 * lesson setting was off at generation time, or the field predates that field existing at all).
 * Deliberately bypasses translationService.translate's Reverso-first/languageMap path — this is
 * whole-sentence text, not single-word lookups, and `from`/`to` are already the app's own ISO
 * codes, which is exactly what Google's endpoint wants directly.
 *
 * Every call here reaches Google unconditionally (there's no Reverso fallback to prefer first),
 * so unlike translate() below, the Preferences > Integrations > "Google Translate" opt-out can't
 * be enforced inside this function without a flag every caller would have to remember to pass —
 * TranslateToggle (the sole caller) gates its own canFallback on that setting instead and never
 * calls this at all when it's off.
 */
export async function translateSentence(text: string, from: string, to: string): Promise<string> {
    const result = await getGoogleTranslation(text, from, to);
    return result.translations[0];
}

export const translationService = {
    /**
     * @param useGoogleFallback Wired to the user's Preferences > Integrations > "Google
     * Translate" toggle (`auth.userSettings.integration_settings.google_translate`, defaults
     * to true) — pass that value through from each call site. When false, a Reverso failure
     * is surfaced as-is instead of silently retrying via Google.
     */
    async translate(text: string, from: string, to: string, useGoogleFallback: boolean = true): Promise<TranslateResult> {
        // A pair Reverso doesn't cover (fa, vi) can only come from Google.
        if (!reversoSupports(from, to)) {
            if (!useGoogleFallback) throw new Error(`Translation from ${from} to ${to} needs Google Translate`);
            return getGoogleTranslation(text, from, to);
        }
        try {
            const result = await getReversoTranslation(text, from as ReversoLanguage, to as ReversoLanguage);
            if (result?.translations?.length) return result;
            throw new Error('Empty translation received');
        } catch (reversoError) {
            if (!useGoogleFallback) {
                throw reversoError instanceof Error ? reversoError : new Error('Translation failed');
            }
            // Reverso failed outright, or came back with nothing usable (e.g. text it doesn't
            // recognize) — fall back to Google rather than surfacing an error the user can't
            // do anything about. Context examples (Reverso-only) are simply absent from a
            // fallback result; every caller already treats those as optional.
            try {
                return await getGoogleTranslation(text, from, to);
            } catch {
                throw reversoError instanceof Error ? reversoError : new Error('Translation failed');
            }
        }
    }
};
